import { Component, defineComponent } from './component-base.js';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * @extends {Component}
 */
class EmailSignupForm extends Component {
  /** @type {{ error_empty?: string, error_invalid?: string }} */
  #translations = {};

  connectedCallback() {
    super.connectedCallback();
    this.#translations = this.#parseTranslations();

    if (this.#revealSuccess()) return;

    const form = this.#getForm();
    const emailInput = this.#getEmailInput();

    if (!form || !emailInput) return;

    form.addEventListener('submit', this.#handleSubmit, { capture: true });
    emailInput.addEventListener('input', this.#handleInput);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const form = this.#getForm();
    const emailInput = this.#getEmailInput();

    form?.removeEventListener('submit', this.#handleSubmit, { capture: true });
    emailInput?.removeEventListener('input', this.#handleInput);
  }

  #revealSuccess() {
    const success = this.querySelector('[data-email-signup-success]');
    if (!(success instanceof HTMLElement)) return false;

    success.focus({ preventScroll: true });
    success.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }

  #getForm() {
    const form = this.refs.form ?? this.querySelector('form');
    return form instanceof HTMLFormElement ? form : null;
  }

  #getEmailInput() {
    const input = this.refs.emailInput ?? this.querySelector('input[type="email"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  #getErrorMessage() {
    const error = this.refs.errorMessage ?? this.querySelector('[ref="errorMessage"]');
    return error instanceof HTMLElement ? error : null;
  }

  #parseTranslations() {
    try {
      return JSON.parse(this.dataset.translations || '{}');
    } catch {
      return {};
    }
  }

  #handleSubmit = (event) => {
    if (!this.#validateEmail()) {
      event.preventDefault();
      event.stopPropagation();
      this.#getEmailInput()?.focus();
    }
  };

  #handleInput = () => {
    const input = this.#getEmailInput();
    if (!input || input.getAttribute('aria-invalid') !== 'true') return;
    this.#validateEmail();
  };

  /**
   * @returns {boolean}
   */
  #validateEmail() {
    const input = this.#getEmailInput();
    if (!input) return false;

    const value = input.value.trim();

    if (!value) {
      this.#showError(this.#translations.error_empty || 'Please enter your email address');
      return false;
    }

    if (!EMAIL_PATTERN.test(value)) {
      this.#showError(this.#translations.error_invalid || 'Please enter a valid email address');
      return false;
    }

    this.#clearError();
    return true;
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const emailInput = this.#getEmailInput();
    const errorMessage = this.#getErrorMessage();

    if (errorMessage) {
      errorMessage.textContent = message;
      errorMessage.classList.remove('hidden');
    }

    if (emailInput) {
      emailInput.setAttribute('aria-invalid', 'true');
      if (errorMessage?.id) {
        emailInput.setAttribute('aria-describedby', errorMessage.id);
      }
    }
  }

  #clearError() {
    const emailInput = this.#getEmailInput();
    const errorMessage = this.#getErrorMessage();

    if (errorMessage) {
      errorMessage.textContent = '';
      errorMessage.classList.add('hidden');
    }

    if (emailInput) {
      emailInput.removeAttribute('aria-invalid');
      emailInput.removeAttribute('aria-describedby');
    }
  }
}

defineComponent('email-signup-form', EmailSignupForm);
